/** Person Links app assignment metadata helpers */

export const PERSON_LINKS_RELATIONSHIP_TYPES = [
  'guardian',
  'sibling',
  'teacher_of_record',
  'counselor',
  'supervisor'
];

/** Section order on User Profile (only types with rows are shown). */
export const PERSON_LINK_DISPLAY_ORDER = [
  'guardian',
  'sibling',
  'teacher_of_record',
  'counselor',
  'supervisor'
];

export const PERSON_LINK_SECTION_LABELS = {
  guardian: 'Guardians',
  sibling: 'Siblings',
  teacher_of_record: 'Teachers of Record',
  counselor: 'Counselors',
  supervisor: 'Supervisors'
};

export const PERSON_LINK_ADD_TITLES = {
  guardian: 'Add Guardian',
  sibling: 'Add Sibling',
  teacher_of_record: 'Add Teacher of Record',
  counselor: 'Add Counselor',
  supervisor: 'Add Supervisor'
};

export const PERSON_LINK_CURRENT_USER_LABELS = {
  guardian: 'Child',
  sibling: 'Sibling (this profile)',
  teacher_of_record: 'Student',
  counselor: 'Student',
  supervisor: 'Employee'
};

export const PERSON_LINK_TARGET_LABELS = {
  guardian: 'Guardian',
  sibling: 'Sibling',
  teacher_of_record: 'Teacher of Record',
  counselor: 'Counselor',
  supervisor: 'Supervisor'
};

export const GUARDIAN_SUBTYPE_OPTIONS = [
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'grandfather', label: 'Grandfather' },
  { value: 'grandmother', label: 'Grandmother' },
  { value: 'step_father', label: 'Step Father' },
  { value: 'step_mother', label: 'Step Mother' },
  { value: 'foster_parent', label: 'Foster Parent' },
  { value: 'other', label: 'Other' }
];

export const SIBLING_SUBTYPE_OPTIONS = [
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'step_brother', label: 'Step Brother' },
  { value: 'step_sister', label: 'Step Sister' },
  { value: 'half_brother', label: 'Half Brother' },
  { value: 'half_sister', label: 'Half Sister' },
  { value: 'other', label: 'Other' }
];

export const GUARDIAN_SUBTYPE_LABELS = Object.fromEntries(
  GUARDIAN_SUBTYPE_OPTIONS.map((o) => [o.value, o.label])
);

export const SIBLING_SUBTYPE_LABELS = Object.fromEntries(
  SIBLING_SUBTYPE_OPTIONS.map((o) => [o.value, o.label])
);

export const PERSON_LINK_SUBTYPE_OPTIONS = {
  guardian: GUARDIAN_SUBTYPE_OPTIONS,
  sibling: SIBLING_SUBTYPE_OPTIONS
};

/** User → user links (persist across group-period contexts). */
export const PERSON_LINKS_USER_TO_USER_TYPES = new Set(['guardian', 'sibling']);

/** metadata.emergency on a user→user link. A badge, not a guardian or sibling type. */

/** UGP↔UGP links must be created via search only (same org; counselor skips period match). */
export const PERSON_LINKS_SEARCH_ONLY_TYPES = new Set([
  'teacher_of_record',
  'counselor',
  'supervisor'
]);

export function isPersonLinkSearchOnlyType(relationshipType) {
  return PERSON_LINKS_SEARCH_ONLY_TYPES.has(String(relationshipType || '').toLowerCase());
}

export function isUserToUserPersonLinkType(relationshipType) {
  return PERSON_LINKS_USER_TO_USER_TYPES.has(String(relationshipType || '').toLowerCase());
}

export function isGuardianPersonLinkType(relationshipType) {
  return String(relationshipType || '').toLowerCase() === 'guardian';
}

export function isSiblingPersonLinkType(relationshipType) {
  return String(relationshipType || '').toLowerCase() === 'sibling';
}

/** Counselor stays ugp↔ugp in the same org. The counselor seat's period does not have to match. */
export function personLinkRequiresPeriodMatch(relationshipType) {
  return String(relationshipType || '').toLowerCase() !== 'counselor';
}

/** Relationship-type cards in the Add Person Link flow */
export const PERSON_LINK_TYPE_CARDS = [
  {
    value: 'guardian',
    label: 'Guardian',
    linkShape: 'User → User',
    subtext:
      'Tied to this person across all group-period contexts — e.g. Mother stays linked whether you view School A Freshman or School B Kindergarten.',
    scopeNote: 'Changes over time are managed only through the relationship start/end dates.'
  },
  {
    value: 'sibling',
    label: 'Sibling',
    linkShape: 'User → User',
    subtext:
      'Brother, sister, step- or half-sibling, etc. — linked to this person across all group-period contexts.',
    scopeNote: 'Use relationship start/end dates when the link changes over time.'
  },
  {
    value: 'teacher_of_record',
    label: 'Teacher of Record',
    linkShape: 'UGP ↔ UGP',
    subtext:
      'Linked for this organization and school period only. The instructor must have an active assignment in the same org and period.',
    scopeNote: null
  },
  {
    value: 'counselor',
    label: 'Counselor',
    linkShape: 'UGP ↔ UGP',
    subtext:
      'Linked on this student enrollment, so the school can still bound it to this term. The counselor needs an active assignment in the same organization; their period can differ, including Permanent.',
    scopeNote: null
  },
  {
    value: 'supervisor',
    label: 'Supervisor',
    linkShape: 'UGP ↔ UGP',
    subtext:
      'Linked for this organization and school period only. The supervisor must have an active assignment in the same org and period.',
    scopeNote: null
  }
];

/** When metadata omits enabled_relationship_types, all types are allowed. */
export function getEnabledPersonLinkTypes(metadata) {
  const raw = metadata?.enabled_relationship_types;
  if (!Array.isArray(raw) || raw.length === 0) {
    return [...PERSON_LINKS_RELATIONSHIP_TYPES];
  }
  const allowed = new Set(PERSON_LINKS_RELATIONSHIP_TYPES);
  const filtered = raw
    .map((t) => String(t || '').trim().toLowerCase())
    .filter((t) => allowed.has(t));
  return filtered.length ? filtered : [...PERSON_LINKS_RELATIONSHIP_TYPES];
}

export function getPersonLinkTypeCardsForMetadata(metadata) {
  const enabled = new Set(getEnabledPersonLinkTypes(metadata));
  return PERSON_LINK_TYPE_CARDS.filter((card) => enabled.has(card.value));
}

export function buildPersonLinksApiUrl({ userGroupsPeriodsId, userId }) {
  const params = new URLSearchParams();
  if (userGroupsPeriodsId) params.set('user_groups_periods_id', String(userGroupsPeriodsId));
  if (userId) params.set('user_id', String(userId));
  return `/api/relationships/person-links?${params.toString()}`;
}

/** Visible property keys on counselor (etc.) rows — from counselor_visible_properties or legacy flag. */
export function getCounselorVisibleProperties(metadata) {
  const raw = metadata?.counselor_visible_properties;
  if (Array.isArray(raw) && raw.length) {
    return raw
      .map((k) => String(k || '').trim().toLowerCase())
      .filter(Boolean);
  }
  if (metadata?.show_counselor_meeting_link === true) {
    return ['meeting_link'];
  }
  return [];
}

/** @deprecated use getCounselorVisibleProperties(metadata).includes('meeting_link') */
export function showCounselorMeetingLink(metadata) {
  return getCounselorVisibleProperties(metadata).includes('meeting_link');
}

export function personLinkResolvedProperty(pl, propertyKey) {
  const key = String(propertyKey || '').toLowerCase();
  if (pl?.resolved_properties?.[key]) return String(pl.resolved_properties[key]).trim();
  if (key === 'meeting_link' && pl?.linked_meeting_link) {
    return String(pl.linked_meeting_link).trim();
  }
  return '';
}

export function personLinkHasResolvedProperty(pl, propertyKey) {
  return !!personLinkResolvedProperty(pl, propertyKey);
}
